import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { ChevronLeft } from 'lucide-react';
import { ROUTES, PARTNER_ID } from '@/constants';
import { useSICReportDetail } from '../../hooks/useSICReportDetail';
import { SICAccountDetailModal } from '../../components/SICAccountDetailModal';
import type { SICReportAccount, SICAccountResultStatus } from '@/types/sic-report.types';

const ACCOUNT_STATUS_VARIANT: Record<SICAccountResultStatus, { label: string; color: string }> = {
    CONFIRMED: { label: 'Confirmado', color: 'bg-emerald-500/20 text-emerald-400' },
    REJECTED: { label: 'Rechazado', color: 'bg-amber-500/20 text-amber-400' },
    ERROR: { label: 'Error', color: 'bg-red-500/20 text-red-400' },
};

export function SICReportDetailPage() {
    const { reportId } = useParams<{ reportId: string }>();
    const [searchParams] = useSearchParams();
    const runDate = searchParams.get('runDate') ?? undefined;
    const navigate = useNavigate();

    const { detail, isLoading, error } = useSICReportDetail(reportId, PARTNER_ID, runDate);
    const [selectedAccount, setSelectedAccount] = useState<SICReportAccount | null>(null);

    const breadcrumbs = [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Reporte SIC', path: ROUTES.SIC_REPORT },
        { label: runDate ?? 'Detalle', path: '#' },
    ];

    return (
        <div className="space-y-4">
            <PageHeader
                title={`Detalle de Reporte SIC — ${runDate ?? ''}`}
                subtitle={`Report ID: ${reportId ?? '—'}`}
                breadcrumbs={breadcrumbs}
            />
            <Button variant="secondary" onClick={() => navigate(ROUTES.SIC_REPORT)} className="inline-flex items-center gap-1">
                <ChevronLeft size={14} /> Volver
            </Button>

            {isLoading && <Skeleton className="h-64" />}
            {error && (
                <Card>
                    <p className="text-red-400">Error: {(error as Error).message}</p>
                </Card>
            )}
            {detail && (
                <Card title="Cuentas procesadas" description={`${detail.accounts.length} cuentas`}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-500">
                                    <th className="pb-2 text-left font-normal">Crédito</th>
                                    <th className="pb-2 text-left font-normal">Tipo</th>
                                    <th className="pb-2 text-left font-normal">Estado</th>
                                    <th className="pb-2 text-left font-normal">Folio CDC</th>
                                    <th className="pb-2 text-left font-normal">Error</th>
                                    <th className="pb-2 text-right font-normal">Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.accounts.map((acc) => {
                                    const v = ACCOUNT_STATUS_VARIANT[acc.status];
                                    return (
                                        <tr key={acc.credit_id} className="border-b border-slate-800/50">
                                            <td className="py-2 font-mono text-xs text-slate-300">{acc.credit_id}</td>
                                            <td className="py-2 text-slate-300">{acc.report_type}</td>
                                            <td className="py-2"><Badge className={v.color}>{v.label}</Badge></td>
                                            <td className="py-2 font-mono text-xs text-slate-400">{acc.cdc_folio ?? '—'}</td>
                                            <td className="py-2 text-xs text-slate-400">{acc.error_message ?? '—'}</td>
                                            <td className="py-2 text-right">
                                                <button
                                                    onClick={() => setSelectedAccount(acc)}
                                                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                                                >
                                                    Ver detalle
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {selectedAccount && (
                <SICAccountDetailModal
                    account={selectedAccount}
                    onClose={() => setSelectedAccount(null)}
                />
            )}
        </div>
    );
}
